import { useState } from 'react';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import type { ProviderQuote } from '../../features/catalog/model';
import type { SortState } from '../../components/SortableTable';
import {
  hasLatencyMetric,
  hasUptimeMetric,
  ProviderRow,
  providerKey,
  servedCount,
} from './ProviderRow';
import { m } from '../../paraglide/messages';

export type SortKey = 'price' | 'latency' | 'uptime' | 'requests';

const FIELD: Record<SortKey, (provider: ProviderQuote) => number> = {
  price: (provider) => provider.price.perRequestUsd,
  latency: (provider) => provider.latencyMs,
  uptime: (provider) => provider.uptimePct,
  requests: servedCount,
};

const hasSortMetric = (provider: ProviderQuote, key: SortKey): boolean =>
  key === 'uptime'
    ? hasUptimeMetric(provider)
    : key === 'latency'
      ? hasLatencyMetric(provider)
      : true;

/** Sorts the rows for display only. It never reorders the call order, which the
 *  gateway authors and the panel reads from the unsorted `providers` array. */
export const orderProviders = (
  providers: ProviderQuote[],
  sort: SortState<SortKey>,
): ProviderQuote[] => {
  const field = FIELD[sort.key];
  return [...providers].sort((a, b) => {
    const aMeasured = hasSortMetric(a, sort.key);
    const bMeasured = hasSortMetric(b, sort.key);
    if (aMeasured !== bMeasured) return aMeasured ? -1 : 1;
    const delta = (field(a) - field(b)) * (sort.dir === 'asc' ? 1 : -1);
    return delta || a.source.name.localeCompare(b.source.name);
  });
};

/** Each provider's share of what this endpoint served, which is what the share
 *  bars are drawn from. A source that stopped being called still served part of
 *  the window, so it counts in the total and the column keeps summing to it.
 *  Zero traffic everywhere leaves every bar empty. */
export const servedShares = (providers: ProviderQuote[]): number[] => {
  const total = providers.reduce((sum, provider) => sum + servedCount(provider), 0);
  return providers.map((provider) => (total > 0 ? servedCount(provider) / total : 0));
};

const GRID_SHARE =
  'grid grid-cols-[40px_minmax(0,1fr)_auto_40px] items-center gap-x-3 lg:grid-cols-[40px_minmax(0,0.9fr)_minmax(0,1.1fr)_96px_80px_72px_136px_40px]';

/** One provider serves everything it is asked, so there is no share to draw
 *  and nothing to be tried before. */
const GRID_SOLO =
  'grid grid-cols-[40px_minmax(0,1fr)_auto_40px] items-center gap-x-3 lg:grid-cols-[40px_minmax(0,1fr)_96px_80px_72px_136px_40px]';

interface SortHeadProps {
  label: string;
  k: SortKey;
  sort: SortState<SortKey>;
  onSort: (key: SortKey) => void;
  className?: string;
}

const SortHead = ({ label, k, sort, onSort, className }: SortHeadProps) => {
  const active = sort.key === k;
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      className={`mono flex min-h-10 items-center justify-end gap-1 text-right text-12 transition-colors hover:text-text ${
        active ? 'text-text' : 'text-text-3'
      }${className ? ` ${className}` : ''}`}
    >
      {label}
      <span className={`text-9 ${active ? 'opacity-100' : 'opacity-0'}`}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </button>
  );
};

export interface ProvidersPanelProps {
  providers: ProviderQuote[];
  /** Sources the gateway is not calling right now. They are listed after the
   *  ones that are, never sorted in among them and never counted as providers:
   *  a call reaches none of them until they recover. */
  notServing?: ProviderQuote[];
}

/** Public provider comparison. A column draws each provider's share of what
 *  this endpoint actually served, and the provider calls are tried on first
 *  carries the accent bar and its own mark - so the call order is shown in the
 *  table rather than stated in a sentence beside it. Rows navigate to the
 *  provider traffic profiles. */
export const ProvidersPanel = ({ providers, notServing = [] }: ProvidersPanelProps) => {
  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'requests', dir: 'desc' });
  const onSort = (key: SortKey) =>
    setSort((previous) =>
      previous.key === key
        ? { key, dir: previous.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'uptime' || key === 'requests' ? 'desc' : 'asc' },
    );
  const solo = providers.length === 1;
  const GRID = solo ? GRID_SOLO : GRID_SHARE;
  // Both lists are drawn from one denominator, so the column stays "share of
  // what this endpoint served" rather than becoming two different fractions.
  const rows = [...orderProviders(providers, sort), ...orderProviders(notServing, sort)];
  const shares = servedShares(rows);
  // The gateway authors `providers` in the order calls are tried in, so the
  // head of that array is what a call hits first however the table is sorted.
  const firstTried = providers.length > 0 ? providerKey(providers[0]) : null;
  const measuredLatency = providers.filter(hasLatencyMetric).map((provider) => provider.latencyMs);
  const measuredUptime = providers.filter(hasUptimeMetric).map((provider) => provider.uptimePct);
  const minLatency = measuredLatency.length > 0 ? Math.min(...measuredLatency) : 0;
  const maxUptime = measuredUptime.length > 0 ? Math.max(...measuredUptime) : 0;
  const withdrawn = new Set(notServing.map(providerKey));

  // No overflow-hidden on the shell: it would clip the price explainer card
  // open on the last row.
  return (
    <div>
      <div className="rounded-12 border border-rule bg-surface">
        <div className={`${GRID} rounded-t-12 border-b border-rule bg-wash px-4 sm:px-5`}>
          <span />
          <span className="mono whitespace-nowrap text-12 text-text-3">
            {m.endpoint_providers_column()}
          </span>
          {solo ? null : (
            <span className="mono hidden min-h-10 items-center whitespace-nowrap text-12 text-text-3 lg:flex">
              {m.endpoint_share_column()}
            </span>
          )}
          <SortHead label={m.endpoint_uptime_column()} k="uptime" sort={sort} onSort={onSort} className="hidden lg:flex" />
          <SortHead label="p50" k="latency" sort={sort} onSort={onSort} className="hidden lg:flex" />
          <SortHead
            label={m.endpoint_served_column()}
            k="requests"
            sort={sort}
            onSort={onSort}
            className="hidden lg:flex"
          />
          <SortHead label={m.endpoint_cost_column()} k="price" sort={sort} onSort={onSort} />
          <span />
        </div>

        {rows.map((provider, index) => {
          // "Best" is a claim about what a call can reach, so a source that is
          // not being called never wins the column.
          const resting = withdrawn.has(providerKey(provider));
          return (
            <ProviderRow
              key={providerKey(provider)}
              provider={provider}
              grid={GRID}
              solo={solo}
              share={shares[index] ?? 0}
              first={!solo && providerKey(provider) === firstTried}
              bestUptime={!resting && hasUptimeMetric(provider) && provider.uptimePct === maxUptime}
              bestLatency={!resting && hasLatencyMetric(provider) && provider.latencyMs === minLatency}
              notServing={resting}
            />
          );
        })}
      </div>
      {notServing.length > 0 ? (
        <p className="mt-3 text-13 text-text-3">
          <ParaglideMessage
            message={m.endpoint_not_serving_note}
            markup={{ b: ({ children }) => <span className="text-text">{children}</span> }}
          />
        </p>
      ) : null}
    </div>
  );
};
